import React, { useState, useEffect, useContext } from 'react';
import { apiPost, apiGet } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import { useForm } from 'react-hook-form';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useTranslation } from 'react-i18next';
import { preventMaxInput } from 'utils/validations';
import formValidation from '../../utils/formValidation';
import { validationRules } from '../../utils/constants';
import ErrorMessage from 'components/ErrorMessage';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import helpers from '../../utils/helpers';
import OImage from 'components/reusable/OImage';
import { validateFile } from 'utils/reusableMethods';
import AuthContext from 'context/AuthContext';

const AccountantOnboardingAdd = ({ allOnboarding, handleCategory, item }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      city: item?.city || ''
    }
  });
  
  // 1. ADDED fetchCompanyCompletionStatus
  const { user, fetchCompanyCompletionStatus } = useContext(AuthContext);
  
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(item?.Country?._id);
  const [selectedState, setSelectedState] = useState(item?.State?._id);
  const [icon, setIcon] = useState(true);
  const [copyData, setCopyData] = useState(false);
  const [HRData, setHRData] = useState(true);
  const [companyData] = useState(user?._id);
  const [bannerCardImg, setBannerCardImg] = useState({ img: null, url: null });
  const [fileMsg, setFileMsg] = useState({ profilePicture: '' });

  function changeIcon() {
    setIcon(!icon);
  }

  const checkFields = () => {
    const hasCountry = Boolean(selectedCountry);
    const hasState = Boolean(selectedState);
    const hasCity = Boolean(watch('city'));
    const hasProfilePicture = Boolean(watch('profilePicture')[0]);

    return hasCountry && hasState && hasCity && !(hasProfilePicture && fileSizeExceedsLimit());
  };

  const fileSizeExceedsLimit = () => {
    const fileSize = (watch('profilePicture')[0]?.size / (1024 * 1024)).toFixed(2);
    return fileSize > 3;
  };

  useEffect(() => {
    const profilePicture = watch('profilePicture')?.[0];

    if (profilePicture) {
      const fileSize = (profilePicture.size / (1024 * 1024)).toFixed(2);
      const banner_msg = validateFile(profilePicture);

      setBannerCardImg({
        url: URL.createObjectURL(profilePicture),
        img: profilePicture
      });

      setFileMsg({
        ...fileMsg,
        profilePicture:
          banner_msg ||
          (fileSize > 3 ? 'Please select an image below 3Mb.' : '')
      });
    }
  }, [watch('profilePicture')]);

  const onSubmit = async (data) => {
    try {
      if (!checkFields()) {
        if (!selectedCountry) setError('country', { type: 'manual', message: 'Please select country' });
        if (!selectedState) setError('state', { type: 'manual', message: 'Please select state' });
        if (!data.city) setError('city', { type: 'manual', message: 'Please enter city name' });
        return;
      }

      const countryObj = countries.find(item => item._id === selectedCountry);
      const formData = new FormData();

      formData.append('companyID', companyData);
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('mobile', data.mobile);
      formData.append('countryCode', countryObj?.countryCode);
      formData.append('address', data.address);
      formData.append('password', data.password);
      if (bannerCardImg?.img) formData.append('profilePicture', bannerCardImg.img);
      formData.append('countryID', selectedCountry);
      formData.append('stateID', selectedState);
      formData.append('city', data.city);

      const res = await apiPost(apiPath.accountantOnboardingAdd, formData);

      if (res?.data?.success) {
        allOnboarding();
        
        // 2. ADDED THIS: Instantly refresh the header status
        if (fetchCompanyCompletionStatus) {
          fetchCompanyCompletionStatus();
        }

        setCopyData(true);
        setHRData(false);
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log('err:', err);
    }
  };

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      setCountries(res?.data?.results?.allCountries || []);
    } catch (error) {
      console.log('error:', error);
    }
  };

  const loadStates = async (countryId) => {
    try {
      setLoadingStates(true);
      const res = await apiGet(`/v1/user/states/${countryId}`, {});
      setStates(res?.data?.results || []);
      setSelectedState(''); // Reset state when country changes
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    getCountryList();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadStates(selectedCountry);
    } else {
      setStates([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  const renderHRForm = () => (
    <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
      <div className='relative w-auto my-6 mx-auto max-w-3xl'>
        <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
          <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[762px]'>
            <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
              <h3 className='text-xl font-semibold'>
                {t('ADD_ACCOUNTANT_ONBOARDING')}
              </h3>
              <button
                className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                onClick={() => handleCategory(false)}
                title='Close'
              >
                <span className='text-[#B8BBBF] text-4xl' title='Close'>
                  ×
                </span>
              </button>
            </div>
            <div className='relative p-6 flex-auto'>
              <div className='grid sm:grid-cols-2'>
                {/* Name Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      name='name'
                      inputLabel={
                        <>
                          {t('ACCOUNTANT_NAME')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      type='text'
                      autoFocus
                      maxLength={50}
                      onInput={e => preventMaxInput(e, 50)}
                      register={register('name', formValidation['accountantName'])}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <OInputField
                      id='email'
                      errors={errors}
                      inputLabel={
                        <>
                          {t('OFFICIAL_EMAIL_ADDRESS')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      wrapperClassName='relative z-0 w-full group'
                      type='text'
                      name='email'
                      maxLength={50}
                      onInput={e => preventMaxInput(e, 50)}
                      register={register('email', formValidation['email'])}
                    />
                  </div>
                </div>

                {/* Country Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <select
                      type='select'
                      id='country'
                      name='country'
                      className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      onChange={e => {
                        setSelectedCountry(e.target.value);
                        clearErrors('country');
                      }}
                      placeholder=' '
                      value={selectedCountry}
                    >
                      <option value={''}>
                        {t('SELECT_COUNTRY_NAME')}{' '}
                        <span className='text-red-500'>*</span>
                      </option>
                      {countries?.length &&
                        countries?.map((i, key) => (
                          <option key={key} value={i._id}>
                            {i.name}
                          </option>
                        ))}
                    </select>
                    <label
                      htmlFor='country'
                      className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                    >
                      {t('COUNTRY')}
                      <span className='text-red-500'>*</span>
                    </label>
                    {errors.country && <ErrorMessage message={errors.country.message} />}
                  </div>
                </div>

                {/* Mobile Number with Country Code */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7 flex'>
                  {countries?.length > 0 &&
                    countries?.map(index => {
                      return (
                        selectedCountry === index?._id && (
                          <div
                            key={index?._id}
                            className='w-[50px] h-[56px] mr-3 block py-4 px-3 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                          >
                            +{index?.countryCode}
                          </div>
                        )
                      )
                    })}

                  <div className='relative z-0 w-full group'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      type='number'
                      name='mobile'
                      id='mobile'
                      inputLabel={
                        <>
                          {t('MOBILE_NUMBER')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      min={0}
                      onKeyDown={e => {
                        if (['-', '+', 'e'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={e => preventMaxInput(e, 15)}
                      register={register('mobile', formValidation['mobile'])}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* State Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <select
                      type='select'
                      name='state'
                      id='state'
                      className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      placeholder=' '
                      value={selectedState}
                      onChange={e => {
                        setSelectedState(e.target.value);
                        clearErrors('state');
                      }}
                      disabled={!selectedCountry || loadingStates}
                    >
                      <option value=''>
                        {t('SELECT_STATE')}
                        <span className='text-red-500'>*</span>
                      </option>
                      {loadingStates ? (
                        <option value='' disabled>Loading states...</option>
                      ) : (
                        states?.length &&
                        states?.map(
                          (i, key) => (
                            <option key={key} value={i._id}>
                              {i.name}
                            </option>
                          )
                        )
                      )}
                    </select>
                    <label
                      htmlFor='state'
                      className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                    >
                      {t('STATE')}
                      <span className='text-red-500'>*</span>
                    </label>
                    {errors.state && <ErrorMessage message={errors.state.message} />}
                  </div>
                </div>

                {/* City Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      name='city'
                      inputLabel={
                        <>
                          {t('CITY')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      type='text'
                      register={register('city', {
                        required: 'Please enter city name',
                        minLength: {
                          value: 2,
                          message: 'City name must be at least 2 characters'
                        },
                        maxLength: {
                          value: 100,
                          message: 'City name cannot exceed 100 characters'
                        },
                        onChange: (e) => clearErrors('city')
                      })}
                      errors={errors}
                      disabled={!selectedState}
                    />
                  </div>
                </div>

                {/* Address Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      name='address'
                      inputLabel={
                        <>
                          {t('ADDRESS')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      type='text'
                      maxLength={300}
                      onInput={e => preventMaxInput(e, 300)}
                      register={register(
                        'address',
                        formValidation['addresses']
                      )}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <input
                      type={icon ? 'password' : 'text'}
                      name='password'
                      id='password'
                      maxLength={15}
                      className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      placeholder=' '
                      autoComplete='new-password'
                      {...register('password', {
                        required: 'Please enter password.',
                        pattern: {
                          value: validationRules.password,
                          message:
                            'Password must contain lowercase,uppercase characters, numbers, special character and must be 8 character long.'
                        }
                      })}
                    />
                    <label
                      htmlFor='password'
                      className='peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                    >
                      {t('O_PASSWORD')}
                      <span className='text-red-500'>*</span>
                    </label>
                    {icon ? (
                      <span
                        className='password_view cursor-pointer absolute top-[18px] right-[20px]'
                        onClick={() => changeIcon()}
                      >
                        <AiFillEyeInvisible />
                      </span>
                    ) : (
                      <span
                        className='password_view absolute top-[18px] right-[20px]'
                        onClick={() => changeIcon()}
                      >
                        <AiFillEye />
                      </span>
                    )}
                    <button
                      className='float-right'
                      onClick={async () => {
                        const pass = await helpers.generatePassword(8);
                        setValue('password', pass);
                        clearErrors('password');
                      }}
                    >
                      Auto generate
                    </button>
                    <ErrorMessage message={errors?.password?.message} />
                  </div>
                </div>

                {/* Profile Picture Field */}
                <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                  <div className='relative z-0 w-full group'>
                    <div className='block py-3 h-14 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'>
                      <input
                        type='file'
                        name='profilePicture'
                        className='form-control'
                        accept='image/png,image/jpeg,image/jpg'
                        {...register('profilePicture', {})}
                      />
                    </div>
                    <label
                      htmlFor='floating_file'
                      className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-1 left-0 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                    >
                      {t('IMAGE')} (Only image, upto 3Mb)
                    </label>
                    <ErrorMessage
                      message={
                        errors?.profilePicture?.message ||
                        fileMsg.profilePicture
                      }
                    />
                    <div className='flex items-center bg-white'>
                      {bannerCardImg?.url && !fileMsg?.profilePicture && (
                        <OImage
                          src={
                            bannerCardImg?.url !== null
                              ? bannerCardImg?.url
                              : URL.createObjectURL(watch('profilePicture')[0])
                          }
                          className='w-24 h-24 mt-5'
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
              <button
                className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150'
                type='button'
                onClick={() => handleCategory(false)}
              >
                {t('O_BACK')}
              </button>
              <button
                className='bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150'
                type='submit'
                onClick={handleSubmit(onSubmit)}
              >
                {t('O_ADD')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCopyData = () => (
    <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
      <div className='relative w-auto my-6 mx-auto max-w-3xl'>
        <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
          <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[500px]'>
            <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
              <h3 className='text-xl font-semibold'>Copy credentials</h3>
              <button
                className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                onClick={() => handleCategory(false)}
                title='Close'
              >
                <span className='text-[#B8BBBF] text-4xl' title='Close'>
                  ×
                </span>
              </button>
            </div>
            <div className='relative p-6 flex-auto'>
              <div className='grid'>
                <div className='d-flex justify-content-center'>
                  <h4 className='text-center text-lg mb-2'>
                    You can view this credentials only once.
                  </h4>
                  <div className='text-center'>
                    <strong className=''> Email ID:</strong> {watch('email')}
                    <br />
                    <strong>Password:</strong> {watch('password')}
                  </div>
                </div>
              </div>
            </div>
            <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
              <button
                className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150'
                type='button'
                onClick={() => {
                  navigator.clipboard.writeText(
                    'Email:' +
                      watch('email') +
                      ' ' +
                      'Password:' +
                      watch('password')
                  );
                  notification.success('Email and password has been copied to your clipboard.');
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {HRData && renderHRForm()}
      {copyData && renderCopyData()}
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  );
};

export default AccountantOnboardingAdd;



// import React, { useState, useEffect, useContext } from 'react';
// import { apiPost, apiGet } from '../../utils/apiFetch';
// import apiPath from '../../utils/pathObj';
// import { useForm } from 'react-hook-form';
// import useToastContext from 'hooks/useToastContext';
// import OInputField from 'components/reusable/OInputField';
// import { useTranslation } from 'react-i18next';
// import { preventMaxInput } from 'utils/validations';
// import formValidation from '../../utils/formValidation';
// import { validationRules } from '../../utils/constants';
// import ErrorMessage from 'components/ErrorMessage';
// import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
// import helpers from '../../utils/helpers';
// import OImage from 'components/reusable/OImage';
// import { validateFile } from 'utils/reusableMethods';
// import AuthContext from 'context/AuthContext';


// const AccountantOnboardingAdd = ({ allOnboarding, handleCategory, item }) => {
//   const { t } = useTranslation();
//   const notification = useToastContext();
//   const {
//     register,
//     handleSubmit,
//     setValue,
//     watch,
//     setError,
//     clearErrors,
//     formState: { errors }
//   } = useForm({
//     mode: 'onChange',
//     shouldFocusError: true,
//     defaultValues: {
//       city: item?.city || ''
//     }
//   });
  
//   const { user } = useContext(AuthContext);
//   const [countries, setCountries] = useState([]);
//   const [states, setStates] = useState([]);
//   const [loadingStates, setLoadingStates] = useState(false);
//   const [selectedCountry, setSelectedCountry] = useState(item?.Country?._id);
//   const [selectedState, setSelectedState] = useState(item?.State?._id);
//   const [icon, setIcon] = useState(true);
//   const [copyData, setCopyData] = useState(false);
//   const [HRData, setHRData] = useState(true);
//   const [companyData] = useState(user?._id);
//   const [bannerCardImg, setBannerCardImg] = useState({ img: null, url: null });
//   const [fileMsg, setFileMsg] = useState({ profilePicture: '' });

//   function changeIcon() {
//     setIcon(!icon);
//   }

//   const checkFields = () => {
//     const hasCountry = Boolean(selectedCountry);
//     const hasState = Boolean(selectedState);
//     const hasCity = Boolean(watch('city'));
//     const hasProfilePicture = Boolean(watch('profilePicture')[0]);

//     return hasCountry && hasState && hasCity && !(hasProfilePicture && fileSizeExceedsLimit());
//   };

//   const fileSizeExceedsLimit = () => {
//     const fileSize = (watch('profilePicture')[0]?.size / (1024 * 1024)).toFixed(2);
//     return fileSize > 3;
//   };

//   useEffect(() => {
//     const profilePicture = watch('profilePicture')?.[0];

//     if (profilePicture) {
//       const fileSize = (profilePicture.size / (1024 * 1024)).toFixed(2);
//       const banner_msg = validateFile(profilePicture);

//       setBannerCardImg({
//         url: URL.createObjectURL(profilePicture),
//         img: profilePicture
//       });

//       setFileMsg({
//         ...fileMsg,
//         profilePicture:
//           banner_msg ||
//           (fileSize > 3 ? 'Please select an image below 3Mb.' : '')
//       });
//     }
//   }, [watch('profilePicture')]);

//   const onSubmit = async (data) => {
//     try {
//       if (!checkFields()) {
//         if (!selectedCountry) setError('country', { type: 'manual', message: 'Please select country' });
//         if (!selectedState) setError('state', { type: 'manual', message: 'Please select state' });
//         if (!data.city) setError('city', { type: 'manual', message: 'Please enter city name' });
//         return;
//       }

//       const countryObj = countries.find(item => item._id === selectedCountry);
//       const formData = new FormData();

//       formData.append('companyID', companyData);
//       formData.append('name', data.name);
//       formData.append('email', data.email);
//       formData.append('mobile', data.mobile);
//       formData.append('countryCode', countryObj?.countryCode);
//       formData.append('address', data.address);
//       formData.append('password', data.password);
//       if (bannerCardImg?.img) formData.append('profilePicture', bannerCardImg.img);
//       formData.append('countryID', selectedCountry);
//       formData.append('stateID', selectedState);
//       formData.append('city', data.city);

//       const res = await apiPost(apiPath.accountantOnboardingAdd, formData);

//       if (res?.data?.success) {
//         allOnboarding();
//         setCopyData(true);
//         setHRData(false);
//         notification.success(res?.data?.message);
//       } else {
//         notification.error(res?.data?.message);
//       }
//     } catch (err) {
//       console.log('err:', err);
//     }
//   };

//   const getCountryList = async () => {
//     try {
//       const res = await apiGet(apiPath.getCountryList, {});
//       setCountries(res?.data?.results?.allCountries || []);
//     } catch (error) {
//       console.log('error:', error);
//     }
//   };

//   const loadStates = async (countryId) => {
//     try {
//       setLoadingStates(true);
//       const res = await apiGet(`/v1/user/states/${countryId}`, {});
//       setStates(res?.data?.results || []);
//       setSelectedState(''); // Reset state when country changes
//     } catch (error) {
//       console.error('Error loading states:', error);
//     } finally {
//       setLoadingStates(false);
//     }
//   };

//   useEffect(() => {
//     getCountryList();
//   }, []);

//   useEffect(() => {
//     if (selectedCountry) {
//       loadStates(selectedCountry);
//     } else {
//       setStates([]);
//       setSelectedState('');
//     }
//   }, [selectedCountry]);

//   const renderHRForm = () => (
//     <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
//       <div className='relative w-auto my-6 mx-auto max-w-3xl'>
//         <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//           <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[762px]'>
//             <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
//               <h3 className='text-xl font-semibold'>
//                 {t('ADD_ACCOUNTANT_ONBOARDING')}
//               </h3>
//               <button
//                 className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
//                 onClick={() => handleCategory(false)}
//                 title='Close'
//               >
//                 <span className='text-[#B8BBBF] text-4xl' title='Close'>
//                   ×
//                 </span>
//               </button>
//             </div>
//             <div className='relative p-6 flex-auto'>
//               <div className='grid sm:grid-cols-2'>
//                 {/* Name Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <OInputField
//                       wrapperClassName='relative z-0 w-full group'
//                       name='name'
//                       inputLabel={
//                         <>
//                           {t('ACCOUNTANT_NAME')}
//                           <span className='text-red-500'>*</span>
//                         </>
//                       }
//                       type='text'
//                       autoFocus
//                       maxLength={50}
//                       onInput={e => preventMaxInput(e, 50)}
//                       register={register('name', formValidation['accountantName'])}
//                       errors={errors}
//                     />
//                   </div>
//                 </div>

//                 {/* Email Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <OInputField
//                       id='email'
//                       errors={errors}
//                       inputLabel={
//                         <>
//                           {t('OFFICIAL_EMAIL_ADDRESS')}
//                           <span className='text-red-500'>*</span>
//                         </>
//                       }
//                       wrapperClassName='relative z-0 w-full group'
//                       type='text'
//                       name='email'
//                       maxLength={50}
//                       onInput={e => preventMaxInput(e, 50)}
//                       register={register('email', formValidation['email'])}
//                     />
//                   </div>
//                 </div>

//                 {/* Country Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <select
//                       type='select'
//                       id='country'
//                       name='country'
//                       className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
//                       onChange={e => {
//                         setSelectedCountry(e.target.value);
//                         clearErrors('country');
//                       }}
//                       placeholder=' '
//                       value={selectedCountry}
//                     >
//                       <option value={''}>
//                         {t('SELECT_COUNTRY_NAME')}{' '}
//                         <span className='text-red-500'>*</span>
//                       </option>
//                       {countries?.length &&
//                         countries?.map((i, key) => (
//                           <option key={key} value={i._id}>
//                             {i.name}
//                           </option>
//                         ))}
//                     </select>
//                     <label
//                       htmlFor='country'
//                       className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
//                     >
//                       {t('COUNTRY')}
//                       <span className='text-red-500'>*</span>
//                     </label>
//                     {errors.country && <ErrorMessage message={errors.country.message} />}
//                   </div>
//                 </div>

//                 {/* Mobile Number with Country Code */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7 flex'>
//                   {countries?.length > 0 &&
//                     countries?.map(index => {
//                       return (
//                         selectedCountry === index?._id && (
//                           <div
//                             key={index?._id}
//                             className='w-[50px] h-[56px] mr-3 block py-4 px-3 text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
//                           >
//                             +{index?.countryCode}
//                           </div>
//                         )
//                       )
//                     })}

//                   <div className='relative z-0 w-full group'>
//                     <OInputField
//                       wrapperClassName='relative z-0 w-full group'
//                       type='number'
//                       name='mobile'
//                       id='mobile'
//                       inputLabel={
//                         <>
//                           {t('MOBILE_NUMBER')}
//                           <span className='text-red-500'>*</span>
//                         </>
//                       }
//                       min={0}
//                       onKeyDown={e => {
//                         if (['-', '+', 'e'].includes(e.key)) {
//                           e.preventDefault();
//                         }
//                       }}
//                       onInput={e => preventMaxInput(e, 15)}
//                       register={register('mobile', formValidation['mobile'])}
//                       errors={errors}
//                     />
//                   </div>
//                 </div>

//                 {/* State Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <select
//                       type='select'
//                       name='state'
//                       id='state'
//                       className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
//                       placeholder=' '
//                       value={selectedState}
//                       onChange={e => {
//                         setSelectedState(e.target.value);
//                         clearErrors('state');
//                       }}
//                       disabled={!selectedCountry || loadingStates}
//                     >
//                       <option value=''>
//                         {t('SELECT_STATE')}
//                         <span className='text-red-500'>*</span>
//                       </option>
//                       {loadingStates ? (
//                         <option value='' disabled>Loading states...</option>
//                       ) : (
//                         states?.length &&
//                         states?.map(
//                           (i, key) => (
//                             <option key={key} value={i._id}>
//                               {i.name}
//                             </option>
//                           )
//                         )
//                       )}
//                     </select>
//                     <label
//                       htmlFor='state'
//                       className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
//                     >
//                       {t('STATE')}
//                       <span className='text-red-500'>*</span>
//                     </label>
//                     {errors.state && <ErrorMessage message={errors.state.message} />}
//                   </div>
//                 </div>

//                 {/* City Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <OInputField
//                       wrapperClassName='relative z-0 w-full group'
//                       name='city'
//                       inputLabel={
//                         <>
//                           {t('CITY')}
//                           <span className='text-red-500'>*</span>
//                         </>
//                       }
//                       type='text'
//                       register={register('city', {
//                         required: 'Please enter city name',
//                         minLength: {
//                           value: 2,
//                           message: 'City name must be at least 2 characters'
//                         },
//                         maxLength: {
//                           value: 100,
//                           message: 'City name cannot exceed 100 characters'
//                         },
//                         onChange: (e) => clearErrors('city')
//                       })}
//                       errors={errors}
//                       disabled={!selectedState}
//                     />
//                   </div>
//                 </div>

//                 {/* Address Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <OInputField
//                       wrapperClassName='relative z-0 w-full group'
//                       name='address'
//                       inputLabel={
//                         <>
//                           {t('ADDRESS')}
//                           <span className='text-red-500'>*</span>
//                         </>
//                       }
//                       type='text'
//                       maxLength={300}
//                       onInput={e => preventMaxInput(e, 300)}
//                       register={register(
//                         'address',
//                         formValidation['addresses']
//                       )}
//                       errors={errors}
//                     />
//                   </div>
//                 </div>

//                 {/* Password Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <input
//                       type={icon ? 'password' : 'text'}
//                       name='password'
//                       id='password'
//                       maxLength={15}
//                       className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
//                       placeholder=' '
//                       autoComplete='new-password'
//                       {...register('password', {
//                         required: 'Please enter password.',
//                         pattern: {
//                           value: validationRules.password,
//                           message:
//                             'Password must contain lowercase,uppercase characters, numbers, special character and must be 8 character long.'
//                         }
//                       })}
//                     />
//                     <label
//                       htmlFor='password'
//                       className='peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
//                     >
//                       {t('O_PASSWORD')}
//                       <span className='text-red-500'>*</span>
//                     </label>
//                     {icon ? (
//                       <span
//                         className='password_view cursor-pointer absolute top-[18px] right-[20px]'
//                         onClick={() => changeIcon()}
//                       >
//                         <AiFillEyeInvisible />
//                       </span>
//                     ) : (
//                       <span
//                         className='password_view absolute top-[18px] right-[20px]'
//                         onClick={() => changeIcon()}
//                       >
//                         <AiFillEye />
//                       </span>
//                     )}
//                     <button
//                       className='float-right'
//                       onClick={async () => {
//                         const pass = await helpers.generatePassword(8);
//                         setValue('password', pass);
//                         clearErrors('password');
//                       }}
//                     >
//                       Auto generate
//                     </button>
//                     <ErrorMessage message={errors?.password?.message} />
//                   </div>
//                 </div>

//                 {/* Profile Picture Field */}
//                 <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//                   <div className='relative z-0 w-full group'>
//                     <div className='block py-3 h-14 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'>
//                       <input
//                         type='file'
//                         name='profilePicture'
//                         className='form-control'
//                         accept='image/png,image/jpeg,image/jpg'
//                         {...register('profilePicture', {})}
//                       />
//                     </div>
//                     <label
//                       htmlFor='floating_file'
//                       className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-1 left-0 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8'
//                     >
//                       {t('IMAGE')} (Only image, upto 3Mb)
//                     </label>
//                     <ErrorMessage
//                       message={
//                         errors?.profilePicture?.message ||
//                         fileMsg.profilePicture
//                       }
//                     />
//                     <div className='flex items-center bg-white'>
//                       {bannerCardImg?.url && !fileMsg?.profilePicture && (
//                         <OImage
//                           src={
//                             bannerCardImg?.url !== null
//                               ? bannerCardImg?.url
//                               : URL.createObjectURL(watch('profilePicture')[0])
//                           }
//                           className='w-24 h-24 mt-5'
//                         />
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
//               <button
//                 className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150'
//                 type='button'
//                 onClick={() => handleCategory(false)}
//               >
//                 {t('O_BACK')}
//               </button>
//               <button
//                 className='bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150'
//                 type='submit'
//                 onClick={handleSubmit(onSubmit)}
//               >
//                 {t('O_ADD')}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   const renderCopyData = () => (
//     <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
//       <div className='relative w-auto my-6 mx-auto max-w-3xl'>
//         <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
//           <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[500px]'>
//             <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
//               <h3 className='text-xl font-semibold'>Copy credentials</h3>
//               <button
//                 className='ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
//                 onClick={() => handleCategory(false)}
//                 title='Close'
//               >
//                 <span className='text-[#B8BBBF] text-4xl' title='Close'>
//                   ×
//                 </span>
//               </button>
//             </div>
//             <div className='relative p-6 flex-auto'>
//               <div className='grid'>
//                 <div className='d-flex justify-content-center'>
//                   <h4 className='text-center text-lg mb-2'>
//                     You can view this credentials only once.
//                   </h4>
//                   <div className='text-center'>
//                     <strong className=''> Email ID:</strong> {watch('email')}
//                     <br />
//                     <strong>Password:</strong> {watch('password')}
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
//               <button
//                 className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150'
//                 type='button'
//                 onClick={() => {
//                   navigator.clipboard.writeText(
//                     'Email:' +
//                       watch('email') +
//                       ' ' +
//                       'Password:' +
//                       watch('password')
//                   );
//                   notification.success('Email and password has been copied to your clipboard.');
//                 }}
//               >
//                 Copy
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <>
//       {HRData && renderHRForm()}
//       {copyData && renderCopyData()}
//       <div className='opacity-25 fixed inset-0 z-40 bg-black' />
//     </>
//   );
// };

// export default AccountantOnboardingAdd;